import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useStepUpApi } from '../hooks/useStepUpApi';
import { useResumeSubscription } from '../hooks/useResumeSubscription';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  Check, 
  X, 
  ArrowLeft, 
  Sparkles, 
  Building2, 
  Users, 
  Zap,
  Crown,
  Shield,
  FileText,
  Bell,
  Webhook,
  Palette,
  Calendar,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw,
  ArrowRight,
  AlertTriangle,
  XCircle,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from '@/utils/portalNotifications';
import api, { clientAPI } from '../api/client';
import {
  formatBillingUsageHint,
  getCapabilityDeniedMessage,
  isCapabilityDeniedApiError,
  useBillingCapabilities,
} from '../utils/billingCapabilityAccess';
import {
  featureCountForPlanBanner,
  isFeatureEnabledForBillingComparison,
  planPropertyLimitForDisplay,
} from '../utils/billingPlanPresentation';
import { PortalLoadingPanel, portalPageRoot, PortalPageWithLifecyclePresentation } from '../components/client/ClientPortalPatterns';
import { useLifecycleRuntime } from '../contexts/LifecycleRuntimeContext';
import { PORTAL_COPY } from '../utils/clientPortalCopy';
import branding from '../config/branding';

// Plan configuration - matches backend plan_registry.py (colours from Pleerity Brand v1.0)
const PLANS = [
  {
    code: 'PLAN_1_SOLO',
    name: 'Solo Landlord',
    description: 'Perfect for DIY landlords managing 1-2 properties',
    monthlyPrice: 19,
    onboardingFee: 49,
    maxProperties: 2,
    color: branding.text.secondary,
    icon: Building2,
    badge: null,
    targetAudience: 'DIY landlords',
  },
  {
    code: 'PLAN_2_PORTFOLIO',
    name: 'Portfolio',
    description: 'For portfolio landlords and small letting agents',
    monthlyPrice: 39,
    onboardingFee: 79,
    maxProperties: 10,
    color: branding.colors.secondary,
    icon: Users,
    badge: 'Most Popular',
    targetAudience: 'Portfolio landlords, small agents',
  },
  {
    code: 'PLAN_3_PRO',
    name: 'Professional',
    description: 'For letting agents, HMOs, and serious operators',
    monthlyPrice: 79,
    onboardingFee: 149,
    maxProperties: 25,
    color: branding.colors.primary,
    icon: Crown,
    badge: 'Full Features',
    targetAudience: 'Letting agents, HMOs',
  },
];

// Feature categories with all features
const FEATURE_CATEGORIES = [
  {
    name: 'Core Features',
    icon: Shield,
    features: [
      { key: 'compliance_dashboard', name: 'Compliance Dashboard', description: 'View property compliance status at a glance' },
      { key: 'compliance_score', name: 'Compliance Score', description: 'Track your compliance score with explanations' },
      { key: 'compliance_calendar', name: 'Compliance Calendar', description: 'View expiry dates in calendar format' },
      { key: 'email_notifications', name: 'Email Notifications', description: 'Receive compliance reminders via email' },
      { key: 'multi_file_upload', name: 'Multi-File Upload', description: 'Upload multiple documents at once' },
      { key: 'score_trending', name: 'Score Trending', description: 'View compliance score history and trends' },
    ],
  },
  {
    name: 'AI Features',
    icon: Sparkles,
    features: [
      { key: 'ai_extraction_basic', name: 'AI Extraction (Basic)', description: 'Auto-extract document type, issue and expiry dates' },
      { key: 'ai_extraction_advanced', name: 'AI Extraction (Advanced)', description: 'Confidence scoring and field validation' },
      { key: 'extraction_review_ui', name: 'Extraction Review UI', description: 'Review and approve AI-extracted data' },
    ],
  },
  {
    name: 'Documents',
    icon: FileText,
    features: [
      { key: 'zip_upload', name: 'ZIP Archive Upload', description: 'Upload documents as a single ZIP archive' },
    ],
  },
  {
    name: 'Reporting',
    icon: FileText,
    features: [
      { key: 'reports_pdf', name: 'PDF Reports', description: 'Download compliance reports as PDF' },
      { key: 'reports_csv', name: 'CSV Reports', description: 'Download compliance data as CSV' },
      { key: 'scheduled_reports', name: 'Scheduled Reports', description: 'Automatically receive reports on schedule' },
    ],
  },
  {
    name: 'Communication',
    icon: Bell,
    features: [
      { key: 'sms_reminders', name: 'SMS Reminders', description: 'Receive compliance reminders via SMS' },
    ],
  },
  {
    name: 'Tenant Portal',
    icon: Users,
    features: [
      { key: 'tenant_portal', name: 'Tenant Portal', description: 'Allow tenants to view property compliance (read-only)' },
    ],
  },
  {
    name: 'Integrations',
    icon: Webhook,
    features: [
      {
        key: 'webhooks',
        name: 'Webhooks & read API',
        description: 'Outbound webhooks (push) and scoped read API keys (HTTP pull) for integrations',
      },
    ],
  },
  {
    name: 'Advanced',
    icon: Palette,
    features: [
      { key: 'white_label_reports', name: 'White-Label Reports', description: 'Custom branding for reports' },
      { key: 'audit_log_export', name: 'Audit Log Export', description: 'Export audit logs for compliance review' },
    ],
  },
];

/** Stripe epoch or missing dates must not surface as 1970 in the UI */
const MIN_VALID_RENEWAL_MS = 946684800000;

function formatRenewalDisplay(isoOrDate) {
  if (isoOrDate == null || isoOrDate === '') return null;
  const t = new Date(isoOrDate).getTime();
  if (Number.isNaN(t) || t < MIN_VALID_RENEWAL_MS) return null;
  return new Date(isoOrDate).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatLastSyncedEnGb(isoOrDate) {
  if (isoOrDate == null || isoOrDate === '') return null;
  const t = new Date(isoOrDate).getTime();
  if (Number.isNaN(t)) return null;
  return new Date(isoOrDate).toLocaleString('en-GB');
}

/** Canonical period end from billing status API (display + ISO aliases). */
function nextRenewalPrimaryDisplay(bs) {
  if (!bs?.has_subscription) return null;
  return (
    bs.next_renewal_date_display ||
    formatRenewalDisplay(bs.next_renewal_date) ||
    formatRenewalDisplay(bs.current_period_end) ||
    null
  );
}

function nextRenewalFallbackLabel(bs, usageRefreshing) {
  if (!bs?.has_subscription) return 'No active subscription';
  const primary = nextRenewalPrimaryDisplay(bs);
  if (primary) return primary;
  if (usageRefreshing) return 'Syncing…';
  const st = (bs.billing_sync_state || '').toLowerCase();
  if (st === 'stripe_error') return 'Could not load from Stripe';
  if (st === 'missing_period_end' || st === 'stale') return 'Not on file — contact support';
  const last = formatLastSyncedEnGb(bs.billing_last_synced_at);
  if (bs.plan_status_display === 'Paid subscription active' || bs.plan_status_display === 'Active') {
    return last ? `Unavailable (last billing sync ${last})` : 'Renewal date unavailable';
  }
  return '—';
}

function nextRenewalEndPhrase(bs) {
  return nextRenewalPrimaryDisplay(bs) || 'the end of your billing period';
}

function isBillingPeriodEndPast(bs) {
  const raw = bs?.current_period_end;
  if (!raw) return false;
  const dt = new Date(raw);
  return !Number.isNaN(dt.getTime()) && dt.getTime() < Date.now();
}

/** Paid-good-standing display states — used so "renewal soon" does not show during grace/retry/cancel. */
function isPaidSubscriptionDisplayHealthy(bs) {
  const b = bs?.billing_status_display;
  if (!b) return false;
  return (
    b === 'Paid subscription active' ||
    b === 'Paid — renewal billing due soon' ||
    b === 'Active'
  );
}

/** Portal API returns billing_status_display / plan_status_display (no internal lifecycle strings). */
function billingStatusLabel(bs) {
  if (bs?.lifecycle_status_label) return bs.lifecycle_status_label;
  return bs?.billing_status_display || (bs?.has_subscription ? '—' : 'No active subscription');
}

function planStatusLabel(bs) {
  return bs?.plan_status_display || (bs?.has_subscription ? '—' : 'No active subscription');
}

const BillingPage = () => {
  const navigate = useNavigate();
  const {
    canViewBilling,
    canViewInvoices,
    canViewPaymentMethods,
    canCheckout,
    canManageSubscription,
    canCancelSubscription,
  } = useBillingCapabilities();
  const { customerExperience, refreshSession } = useLifecycleRuntime();
  const billingStepUp = useStepUpApi();
  const [searchParams] = useSearchParams();
  const [usageRefreshing, setUsageRefreshing] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [upgrading, setUpgrading] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelContextSnapshot, setCancelContextSnapshot] = useState(null);
  const [cancelContextLoading, setCancelContextLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [billingMainTab, setBillingMainTab] = useState('account');
  const [paymentMethodInfo, setPaymentMethodInfo] = useState(null);
  const [pdfReceipts, setPdfReceipts] = useState([]);
  const [portalOpening, setPortalOpening] = useState(false);
  const [planCatalog, setPlanCatalog] = useState(null);

  const billingUsageHint = useMemo(() => formatBillingUsageHint(billingStatus), [billingStatus]);

  const highlightPlan = searchParams.get('upgrade_to');

  const displayPlans = useMemo(() => {
    if (!planCatalog || planCatalog.length === 0) return PLANS;
    const byCode = Object.fromEntries(planCatalog.map((p) => [p.code, p]));
    return PLANS.map((p) => {
      const api = byCode[p.code];
      if (!api) return p;
      return {
        ...p,
        name: api.name ?? p.name,
        description: api.description ?? p.description,
        monthlyPrice: api.monthly_price != null ? Number(api.monthly_price) : p.monthlyPrice,
        onboardingFee: api.onboarding_fee != null ? Number(api.onboarding_fee) : p.onboardingFee,
        maxProperties: api.max_properties != null ? api.max_properties : p.maxProperties,
        color: api.color ?? p.color,
        badge: api.badge !== undefined ? api.badge : p.badge,
        targetAudience: api.target_audience ?? p.targetAudience,
      };
    });
  }, [planCatalog]);

  const fetchBillingStatus = useCallback(async () => {
    if (!canViewBilling) return;
    try {
      const response = await api.get('/billing/status');
      setBillingStatus(response.data);
      if (response.data?.current_plan_code) {
        setCurrentPlan(response.data.current_plan_code);
      }
    } catch (error) {
      if (isCapabilityDeniedApiError(error)) {
        toast.error(getCapabilityDeniedMessage(error, 'Failed to load billing status'));
      } else {
        console.error('Failed to fetch billing status:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [canViewBilling]);

  const { resumeSubscription, resuming: resumingSubscription, stepUpModal: resumeStepUpModal } =
    useResumeSubscription({
      canManageSubscription,
      onSuccess: async () => {
        await fetchBillingStatus();
        await refreshSession('subscription_resumed');
      },
    });

  const fetchInvoices = useCallback(async () => {
    if (!canViewInvoices) {
      setInvoices([]);
      return;
    }
    try {
      const response = await api.get('/billing/invoices');
      setInvoices(response.data.invoices || []);
    } catch (error) {
      if (isCapabilityDeniedApiError(error)) {
        setInvoices([]);
        toast.error(getCapabilityDeniedMessage(error, 'Failed to load invoices'));
      } else {
        console.error('Failed to fetch invoices:', error);
      }
    }
  }, [canViewInvoices]);

  const handleRefreshUsage = useCallback(async () => {
    setUsageRefreshing(true);
    try {
      if (canViewBilling) {
        await fetchBillingStatus();
      }
      toast.success('Usage data updated');
    } finally {
      setUsageRefreshing(false);
    }
  }, [canViewBilling, fetchBillingStatus]);

  useEffect(() => {
    if (canViewBilling) {
      fetchBillingStatus();
    } else {
      setLoading(false);
    }
    const loadPlans = async () => {
      try {
        const res = await api.get('/billing/plans');
        setPlanCatalog(res.data?.plans || []);
      } catch {
        setPlanCatalog(null);
      }
    };
    loadPlans();
    // Expand all categories by default
    const expanded = {};
    FEATURE_CATEGORIES.forEach(cat => {
      expanded[cat.name] = true;
    });
    setExpandedCategories(expanded);
  }, [canViewBilling, fetchBillingStatus]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'account' || tab === 'plans') {
      setBillingMainTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    const checkoutReturn = searchParams.get('checkout');
    if (!checkoutReturn) return;
    if (checkoutReturn === 'success') {
      toast.success('Payment received', {
        description: 'Your plan change is being confirmed. This may take a moment.',
      });
      fetchBillingStatus();
    } else if (checkoutReturn === 'cancelled') {
      toast.info('Checkout cancelled', {
        description: 'No changes were made. You can choose a plan again when ready.',
      });
    }
    navigate('/settings/billing', { replace: true });
  }, [searchParams, navigate, canViewBilling, fetchBillingStatus]);

  useEffect(() => {
    if (!canViewInvoices) {
      setInvoices([]);
      return;
    }
    if (billingMainTab === 'account') {
      fetchInvoices();
    }
  }, [canViewInvoices, billingMainTab, fetchInvoices]);

  useEffect(() => {
    if (billingMainTab !== 'account') return;
    const loadPm = async () => {
      if (!canViewPaymentMethods) {
        setPaymentMethodInfo(null);
        return;
      }
      try {
        const res = await api.get('/billing/payment-method-summary');
        setPaymentMethodInfo(res.data);
      } catch (e) {
        if (isCapabilityDeniedApiError(e)) {
          setPaymentMethodInfo(null);
        } else {
          setPaymentMethodInfo(null);
        }
      }
    };
    loadPm();
  }, [billingMainTab, canViewPaymentMethods]);

  useEffect(() => {
    if (billingMainTab !== 'account' || !canViewInvoices) {
      setPdfReceipts([]);
      return;
    }
    const loadPdf = async () => {
      try {
        const res = await api.get('/client/billing/receipts');
        setPdfReceipts(res.data.receipts || []);
      } catch {
        setPdfReceipts([]);
      }
    };
    loadPdf();
  }, [billingMainTab, canViewInvoices]);

  useEffect(() => {
    if (!showCancelModal) return;
    let cancelled = false;
    setCancelContextLoading(true);
    setCancelContextSnapshot(null);
    clientAPI
      .getProtectionSnapshot()
      .then((res) => {
        if (!cancelled) setCancelContextSnapshot(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setCancelContextSnapshot(null);
      })
      .finally(() => {
        if (!cancelled) setCancelContextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showCancelModal]);

  const openBillingPortal = async () => {
    if (!canManageSubscription) return;
    setPortalOpening(true);
    try {
      const origin = window.location.origin;
      const res = await billingStepUp.request((headers) =>
        api.post('/billing/portal', {}, { headers: { ...headers, Origin: origin } })
      );
      if (res.data?.portal_url) {
        window.location.href = res.data.portal_url;
      } else if (res.data?.checkout_url) {
        toast.success('Redirecting to Stripe…', {
          description: res.data.recovery_guidance || 'Complete payment to restore your subscription.',
        });
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Could not open billing portal');
      }
    } catch (e) {
      if (e?.message === 'step_up_cancelled') return;
      const driftDetail = e.response?.data?.detail;
      const driftFallback =
        e.response?.status === 409 &&
        driftDetail &&
        typeof driftDetail === 'object' &&
        driftDetail.fallback === 'checkout' &&
        canCheckout &&
        currentPlan;
      if (driftFallback) {
        try {
          const response = await billingStepUp.request((headers) =>
            api.post('/billing/checkout', { plan_code: currentPlan }, { headers })
          );
          if (response.data?.checkout_url) {
            toast.success('Redirecting to Stripe…', {
              description: 'Complete payment to restore your subscription.',
            });
            window.location.href = response.data.checkout_url;
            return;
          }
        } catch (checkoutErr) {
          if (checkoutErr?.message === 'step_up_cancelled') return;
          if (isCapabilityDeniedApiError(checkoutErr)) {
            toast.error(getCapabilityDeniedMessage(checkoutErr, 'Billing recovery unavailable'));
            return;
          }
        }
      }
      if (isCapabilityDeniedApiError(e)) {
        toast.error(getCapabilityDeniedMessage(e, 'Billing portal unavailable'));
      } else {
        const msg =
          typeof driftDetail === 'object' && driftDetail?.message
            ? driftDetail.message
            : e.response?.data?.detail;
        toast.error(typeof msg === 'string' ? msg : 'Billing portal unavailable');
      }
    } finally {
      setPortalOpening(false);
    }
  };

  const handleCancelSubscription = async (cancelImmediately = false) => {
    if (!canCancelSubscription) return;
    setCancelling(true);
    try {
      await billingStepUp.request((headers) =>
        api.post('/billing/cancel', { cancel_immediately: cancelImmediately }, { headers })
      );

      if (cancelImmediately) {
        toast.success('Subscription cancelled', {
          description: 'Your subscription has been cancelled immediately.',
        });
      } else {
        toast.success('Cancellation scheduled', {
          description: 'Your subscription will end at the current billing period.',
        });
      }
      
      setShowCancelModal(false);
      await fetchBillingStatus();
    } catch (error) {
      if (error?.message === 'step_up_cancelled') {
        return;
      }
      console.error('Cancel error:', error);
      if (isCapabilityDeniedApiError(error)) {
        toast.error(getCapabilityDeniedMessage(error, 'Failed to cancel subscription'));
      } else {
        const errorMessage = error.response?.data?.detail || 'Failed to cancel subscription';
        toast.error(errorMessage);
      }
    } finally {
      setCancelling(false);
    }
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const handlePlanChange = async (planCode) => {
    if (!canCheckout) return;
    if (planCode === currentPlan) {
      toast.info('You are already on this plan');
      return;
    }

    const currentPlanIndex = displayPlans.findIndex((p) => p.code === currentPlan);
    const targetPlanIndex = displayPlans.findIndex((p) => p.code === planCode);
    const isDowngrade = currentPlanIndex >= 0 && targetPlanIndex >= 0 && targetPlanIndex < currentPlanIndex;

    setUpgrading(planCode);

    try {
      const response = await billingStepUp.request((headers) =>
        api.post('/billing/checkout', { plan_code: planCode }, { headers })
      );

      toast.success('Redirecting to Stripe…', {
        description: isDowngrade
          ? 'Confirm your plan change. Downgrades typically apply from the next billing period.'
          : 'You will confirm payment or plan change in Stripe.',
      });
      
      // Redirect to checkout or billing portal
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      } else {
        toast.error('No checkout URL received');
        setUpgrading(null);
      }
      
    } catch (error) {
      if (error?.message === 'step_up_cancelled') {
        setUpgrading(null);
        return;
      }
      console.error('Plan change error:', error);
      const detail = error.response?.data?.detail;
      if (isCapabilityDeniedApiError(error)) {
        toast.error(getCapabilityDeniedMessage(error, 'Failed to start plan change'));
      } else {
        const errorMessage =
          typeof detail === 'object' && detail?.message
            ? detail.message
            : typeof detail === 'string'
              ? detail
              : 'Failed to start plan change';
        toast.error(errorMessage);
      }
      setUpgrading(null);
    }
  };

  const getPlanStatus = (planCode) => {
    if (planCode === currentPlan) return 'current';

    const currentPlanIndex = displayPlans.findIndex((p) => p.code === currentPlan);
    const targetPlanIndex = displayPlans.findIndex((p) => p.code === planCode);
    if (currentPlanIndex < 0 || targetPlanIndex < 0) {
      return 'upgrade';
    }
    if (targetPlanIndex > currentPlanIndex) return 'upgrade';
    return 'downgrade';
  };


  if (loading) {
    return (
      <div className={portalPageRoot} data-testid="billing-loading">
        <PortalLoadingPanel message={PORTAL_COPY.loadingBilling} />
      </div>
    );
  }

  return (
    <PortalPageWithLifecyclePresentation data-testid="billing-page">
      <div className="max-w-6xl mx-auto px-4 pt-4 pb-2">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => (window.history.length > 2 ? navigate(-1) : navigate('/dashboard'))}
            className="p-2 min-h-11 min-w-11 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors shrink-0"
            data-testid="back-btn"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-midnight-blue">Billing</h1>
            <p className="text-sm text-gray-500">
              {billingMainTab === 'account'
                ? 'Subscription summary, billing history, and payment details'
                : 'Compare plans and upgrade your subscription'}
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-8 border-b border-gray-200 pb-4" data-testid="billing-main-tabs">
          <button
            type="button"
            onClick={() => setBillingMainTab('account')}
            className={`px-4 py-2 min-h-11 text-sm font-medium rounded-lg transition-colors w-full sm:w-auto ${
              billingMainTab === 'account'
                ? 'bg-midnight-blue text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Billing &amp; history
          </button>
          <button
            type="button"
            onClick={() => setBillingMainTab('plans')}
            className={`px-4 py-2 min-h-11 text-sm font-medium rounded-lg transition-colors w-full sm:w-auto ${
              billingMainTab === 'plans'
                ? 'bg-midnight-blue text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Plans &amp; upgrades
          </button>
        </div>

        <div
          className="flex flex-wrap items-center justify-between gap-3 mb-6 p-3 bg-white border border-gray-200 rounded-lg"
          data-testid="billing-usage-row"
        >
          {billingUsageHint ? (
            <p className="text-sm text-gray-600 flex-1 min-w-[200px]" data-testid="billing-usage-context">
              {billingUsageHint}
            </p>
          ) : (
            <span className="text-sm text-gray-500 flex-1 min-w-[200px]">
              Portfolio usage syncs with your account. Refresh to pull the latest property count and limits.
            </span>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={handleRefreshUsage}
            disabled={usageRefreshing}
            data-testid="billing-refresh-usage"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${usageRefreshing ? 'animate-spin' : ''}`} />
            Refresh usage
          </Button>
        </div>

        {billingMainTab === 'account' ? (
          <div className="space-y-6" data-testid="billing-account-tab">
            {billingStatus?.has_subscription && billingStatus?.billing_status_display === 'Payment issue' && (
              <>
                {billingStatus.grace_period_ends_at ? (
                  <Alert className="border-amber-300 bg-amber-50" data-testid="billing-grace-alert">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <AlertDescription className="text-amber-900 text-sm">
                      <strong>Payment issue</strong> — we could not charge your card. Update your payment method in
                      Stripe below. Resolve payment by{' '}
                      {new Date(billingStatus.grace_period_ends_at).toLocaleDateString('en-GB')}. Some automations (SMS,
                      webhooks, scheduled reports) stay paused until payment succeeds; core compliance remains available.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="border-red-200 bg-red-50" data-testid="billing-limited-alert">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    <AlertDescription className="text-red-900 text-sm">
                      <strong>Payment issue</strong> — update billing to restore full access. Core compliance features
                      stay available where your plan allows.
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
            {billingStatus?.has_subscription &&
              billingStatus?.renewal_soon &&
              !billingStatus?.cancel_at_period_end &&
              isPaidSubscriptionDisplayHealthy(billingStatus) && (
                <Alert className="border-teal-200 bg-teal-50/50" data-testid="billing-renewing-alert">
                  <Calendar className="w-4 h-4 text-teal-700" />
                  <AlertDescription className="text-teal-900 text-sm">
                    {billingStatus.charge_automatically === false ? (
                      <>
                        Your billing period renews soon (
                        {nextRenewalPrimaryDisplay(billingStatus) || 'see date below'}
                        ). Complete payment when invoiced to avoid interruption.
                      </>
                    ) : (
                      <>
                        Your subscription renews soon (
                        {nextRenewalPrimaryDisplay(billingStatus) || 'see date below'}
                        ). Payment is automatic — confirm your card on file is valid.
                      </>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            {/* A. Subscription summary */}
            <Card data-testid="subscription-summary-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Subscription summary</CardTitle>
                <CardDescription>Your Compliance Vault Pro subscription status</CardDescription>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Current plan</p>
                  <p className="font-medium text-midnight-blue">
                    {displayPlans.find((p) => p.code === currentPlan)?.name || currentPlan || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Plan status</p>
                  <p className="font-medium">{planStatusLabel(billingStatus)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Billing status</p>
                  <p className="font-medium">{billingStatusLabel(billingStatus)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Next renewal date</p>
                  <p className="font-medium">
                    {nextRenewalFallbackLabel(billingStatus, usageRefreshing)}
                  </p>
                  {billingStatus?.has_subscription && billingStatus?.billing_last_synced_at && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Last billing sync: {formatLastSyncedEnGb(billingStatus.billing_last_synced_at)}
                    </p>
                  )}
                </div>
                {billingStatus?.has_subscription && customerExperience?.current_state_label && (
                  <div>
                    <p className="text-gray-500">Access status</p>
                    <p className="font-medium">{customerExperience.current_state_label}</p>
                  </div>
                )}
                {billingStatus?.has_subscription && billingStatus?.grace_period_summary && (
                  <div className="sm:col-span-2">
                    <p className="text-gray-500">Grace period</p>
                    <p className="font-medium text-amber-900 text-sm">{billingStatus.grace_period_summary}</p>
                  </div>
                )}
                {billingStatus?.has_subscription &&
                  (billingStatus?.last_payment_display ||
                    billingStatus?.last_payment_at ||
                    billingStatus?.last_payment_status) && (
                    <div className="sm:col-span-2">
                      <p className="text-gray-500">Last successful payment</p>
                      <p className="font-medium">
                        {billingStatus.last_payment_display ||
                          (billingStatus.last_payment_at
                            ? new Date(billingStatus.last_payment_at).toLocaleString('en-GB')
                            : null) ||
                          '—'}
                      </p>
                      {billingStatus.last_payment_status && (
                        <p className="text-xs text-gray-500 mt-0.5">Status: {billingStatus.last_payment_status}</p>
                      )}
                      {billingStatus.open_invoice_status && (
                        <p className="text-xs text-amber-800 mt-1">
                          Open invoice: {billingStatus.open_invoice_status}
                          {billingStatus.stripe_next_payment_attempt_at
                            ? ` · Next retry ${new Date(billingStatus.stripe_next_payment_attempt_at).toLocaleString('en-GB')}`
                            : ''}
                        </p>
                      )}
                    </div>
                  )}
                <div>
                  <p className="text-gray-500">Monthly price</p>
                  <p className="font-medium">
                    {billingStatus?.monthly_price_display ||
                      (currentPlan && displayPlans.find((p) => p.code === currentPlan) ? (
                        <>£{displayPlans.find((p) => p.code === currentPlan).monthlyPrice}/month</>
                      ) : (
                        '—'
                      ))}
                  </p>
                </div>
                {billingStatus?.has_subscription &&
                  (billingStatus.setup_fee_state === 'applies_first_cycle' || billingStatus.setup_fee_state === 'paid') && (
                    <div>
                      <p className="text-gray-500">Setup fee</p>
                      <p className="font-medium">
                        {billingStatus.setup_fee_state === 'paid'
                          ? `Paid${billingStatus.setup_fee_display ? ` (${billingStatus.setup_fee_display})` : ''}`
                          : billingStatus.setup_fee_display || '—'}
                      </p>
                    </div>
                  )}
                <div>
                  <p className="text-gray-500">Properties</p>
                  <p className="font-medium">
                    {canViewBilling && billingStatus?.has_subscription
                      ? `${billingStatus.properties_used ?? '—'} / ${billingStatus.properties_limit ?? '—'}`
                      : '—'}
                  </p>
                </div>
                {billingStatus?.has_subscription && billingStatus?.renewal_customer_copy && (
                  <div className="sm:col-span-2">
                    <p className="text-gray-500">Renewal</p>
                    <p className="font-medium text-gray-800">{billingStatus.renewal_customer_copy}</p>
                  </div>
                )}
                {billingStatus?.has_subscription &&
                  Array.isArray(billingStatus.billing_operational_narrative_lines) &&
                  billingStatus.billing_operational_narrative_lines.length > 0 && (
                    <div className="sm:col-span-2 rounded-md border border-slate-200 bg-slate-50 p-3">
                      <p className="text-gray-500 text-sm mb-2">Operational summary</p>
                      <ol className="list-decimal list-inside space-y-1 text-sm text-gray-800">
                        {billingStatus.billing_operational_narrative_lines.map((line, idx) => (
                          <li key={idx}>{line}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                {billingStatus?.has_subscription && billingStatus?.billing_sync_visibility_note && (
                  <div className="sm:col-span-2">
                    <p className="text-gray-500">Billing sync status</p>
                    <p
                      className={`text-sm ${
                        /incomplete|stripe_error|may be incomplete/i.test(billingStatus.billing_sync_visibility_note)
                          ? 'text-amber-900'
                          : 'text-gray-600'
                      }`}
                    >
                      {billingStatus.billing_sync_visibility_note}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* C. Payment method — Stripe portal is source of truth */}
            <Card data-testid="payment-method-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Payment method</CardTitle>
                <CardDescription>
                  Card and billing details are managed securely in Stripe. We only show a masked summary here when
                  available.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!billingStatus?.has_subscription ? (
                  <p className="text-sm text-gray-600">Subscribe to a plan to add a payment method.</p>
                ) : !canViewPaymentMethods ? (
                  <p className="text-sm text-gray-600">Payment method details are not available for this account state.</p>
                ) : (
                  <>
                    {paymentMethodInfo?.display && (
                      <p className="text-sm font-medium text-midnight-blue">{paymentMethodInfo.display}</p>
                    )}
                    {paymentMethodInfo?.message && !paymentMethodInfo?.display && (
                      <p className="text-sm text-gray-600">{paymentMethodInfo.message}</p>
                    )}
                    {!paymentMethodInfo?.display && !paymentMethodInfo?.message && paymentMethodInfo?.available === false && (
                      <p className="text-sm text-gray-600">Unable to load card summary. You can still manage payment methods in the portal.</p>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      className="border-electric-teal text-electric-teal"
                      onClick={openBillingPortal}
                      disabled={!canManageSubscription || portalOpening}
                    >
                      {portalOpening ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                      Update payment method in Stripe
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Unified billing history: Stripe invoices + Pleerity PDF receipts */}
            <Card data-testid="billing-history-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Billing history</CardTitle>
                <CardDescription>
                  Stripe invoices (paid charges) and Pleerity PDF receipts generated from subscription checkouts.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-10">
                {!canViewInvoices ? (
                  <p className="text-sm text-gray-600 py-2">
                    Billing history is not available for this account state.
                  </p>
                ) : !billingStatus?.has_subscription ? (
                  <p className="text-sm text-gray-600 py-2">
                    No active subscription — billing history appears after you subscribe and pay.
                  </p>
                ) : (
                  <>
                    <div data-testid="stripe-invoice-history">
                      <h3 className="text-sm font-semibold text-midnight-blue mb-1">Stripe invoices</h3>
                      <p className="text-xs text-gray-500 mb-3">Line items and amounts as recorded by Stripe.</p>
                      {invoices.length === 0 ? (
                        <div className="py-8 text-center text-gray-500 text-sm border border-dashed rounded-lg bg-gray-50/50">
                          No Stripe invoices loaded yet. They appear after successful payments.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {invoices.map((inv) => (
                            <Card key={inv.id}>
                              <CardContent className="pt-4 pb-4">
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                  <span className="text-sm font-medium text-gray-700">{inv.number || inv.id}</span>
                                  <span className="text-sm text-gray-500">
                                    {inv.created ? new Date(inv.created * 1000).toLocaleDateString('en-GB') : ''}
                                  </span>
                                </div>
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div className="space-y-1">
                                    {(inv.lines || []).map((line, idx) => (
                                      <div
                                        key={idx}
                                        className="text-sm text-gray-600 flex flex-wrap justify-between gap-2"
                                      >
                                        <span>{line.description}</span>
                                        <span className="text-gray-800 font-medium shrink-0">
                                          £{((line.amount_cents || 0) / 100).toFixed(2)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                  <span className="font-semibold text-midnight-blue">
                                    £{((inv.amount_paid || 0) / 100).toFixed(2)}
                                  </span>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>

                    <div data-testid="pleerity-pdf-receipts">
                      <h3 className="text-sm font-semibold text-midnight-blue mb-1">Pleerity receipts (PDF)</h3>
                      <p className="text-xs text-gray-500 mb-3">Official PDF receipts stored in your account.</p>
                      {pdfReceipts.length === 0 ? (
                        <div className="text-center py-10 text-gray-500 border border-dashed rounded-lg bg-gray-50/50">
                          <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
                          <p className="text-sm">No PDF receipts on file yet.</p>
                          <p className="text-xs mt-1">Created after each subscription checkout.</p>
                        </div>
                      ) : (
                        <>
                          <div className="md:hidden space-y-3">
                            {pdfReceipts.map((r) => (
                              <div
                                key={r.invoice_number || r.stripe_checkout_session_id}
                                className="rounded-lg border border-gray-200 p-3 bg-white"
                              >
                                <p className="font-mono text-xs text-midnight-blue">{r.invoice_number || '—'}</p>
                                <p className="text-sm text-gray-700 mt-1">{r.line_summary || 'Subscription receipt'}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {r.date_issued ? new Date(r.date_issued).toLocaleDateString('en-GB') : '—'} · {r.amount_display || '—'} · {r.payment_status}
                                </p>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-3 min-h-11 w-full"
                                  disabled={!canViewInvoices || !r.invoice_number}
                                  onClick={async () => {
                                    if (!r.invoice_number) return;
                                    try {
                                      const response = await api.get(
                                        `/client/billing/receipt/${encodeURIComponent(r.invoice_number)}/download`,
                                        { responseType: 'blob' }
                                      );
                                      const blob = new Blob([response.data], { type: 'application/pdf' });
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `${r.invoice_number}.pdf`;
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      toast.success('Download started');
                                    } catch {
                                      toast.error('Download failed');
                                    }
                                  }}
                                >
                                  Download PDF
                                </Button>
                              </div>
                            ))}
                          </div>
                          <div className="hidden md:block overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b text-left text-gray-500">
                                  <th className="py-2 pr-4">Invoice #</th>
                                  <th className="py-2 pr-4">Date</th>
                                  <th className="py-2 pr-4">Description</th>
                                  <th className="py-2 pr-4">Amount</th>
                                  <th className="py-2 pr-4">Status</th>
                                  <th className="py-2 text-right">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pdfReceipts.map((r) => (
                                  <tr key={r.invoice_number || r.stripe_checkout_session_id} className="border-b border-gray-100">
                                    <td className="py-3 pr-4 font-mono text-xs">{r.invoice_number}</td>
                                    <td className="py-3 pr-4">
                                      {r.date_issued ? new Date(r.date_issued).toLocaleDateString('en-GB') : '—'}
                                    </td>
                                    <td className="py-3 pr-4 text-gray-700">
                                      {r.line_summary || 'Subscription receipt'}
                                    </td>
                                    <td className="py-3 pr-4">{r.amount_display || '—'}</td>
                                    <td className="py-3 pr-4">{r.payment_status}</td>
                                    <td className="py-3 text-right">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={!canViewInvoices || !r.invoice_number}
                                        onClick={async () => {
                                          if (!r.invoice_number) return;
                                          try {
                                            const response = await api.get(
                                              `/client/billing/receipt/${encodeURIComponent(r.invoice_number)}/download`,
                                              { responseType: 'blob' }
                                            );
                                            const blob = new Blob([response.data], { type: 'application/pdf' });
                                            const url = window.URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = `${r.invoice_number}.pdf`;
                                            a.click();
                                            window.URL.revokeObjectURL(url);
                                            toast.success('Download started');
                                          } catch {
                                            toast.error('Download failed');
                                          }
                                        }}
                                      >
                                        PDF
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="border-teal-100 bg-teal-50/30" data-testid="billing-support-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Billing support</CardTitle>
                <CardDescription>We&apos;re here if something doesn&apos;t look right.</CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-gray-700 space-y-2">
                <p>
                  Email{' '}
                  <a className="text-electric-teal font-medium hover:underline" href="mailto:info@pleerityenterprise.co.uk">
                    info@pleerityenterprise.co.uk
                  </a>
                </p>
                <p className="text-xs text-gray-600">Include your account email and any invoice numbers if you need help with a charge.</p>
              </CardContent>
            </Card>

            <p className="text-sm text-gray-600">
              Paid service orders (one-off) have receipts in{' '}
              <Link to="/orders" className="text-electric-teal font-medium hover:underline">
                Orders
              </Link>
              .
            </p>
          </div>
        ) : (
          <>
        {/* Cancellation Pending Notice */}
        {billingStatus?.cancel_at_period_end && (
          <Alert className="mb-6 border-amber-200 bg-amber-50" data-testid="cancellation-notice">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>{isBillingPeriodEndPast(billingStatus) ? 'Updating your subscription status' : 'Cancellation Scheduled'}</strong>
              <p className="mt-1">
                {isBillingPeriodEndPast(billingStatus)
                  ? 'Your subscription status is being updated. This usually completes within a few minutes.'
                  : `Your subscription will end on ${nextRenewalEndPhrase(billingStatus)}. You'll continue to have full access until then.`}
              </p>
              {canManageSubscription && (
                <div className="mt-3">
                  <Button
                    type="button"
                    size="sm"
                    onClick={resumeSubscription}
                    disabled={resumingSubscription}
                    data-testid="billing-keep-subscription"
                  >
                    {resumingSubscription ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" aria-hidden />
                        Keeping subscription…
                      </>
                    ) : (
                      'Keep subscription'
                    )}
                  </Button>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Current Plan Banner */}
        {currentPlan && (
          <div className="bg-gradient-to-r from-midnight-blue to-midnight-blue/90 text-white rounded-2xl p-6 mb-8" data-testid="current-plan-banner">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-300 mb-1">Your Current Plan</p>
                <h2 className="text-2xl font-bold">
                  {displayPlans.find((p) => p.code === currentPlan)?.name || currentPlan}
                </h2>
                <p className="text-sm text-gray-300 mt-1">
                  {planPropertyLimitForDisplay(currentPlan, billingStatus, displayPlans)} properties •{' '}
                  {featureCountForPlanBanner(currentPlan)} plan features included
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">
                  £{displayPlans.find((p) => p.code === currentPlan)?.monthlyPrice || 0}
                  <span className="text-lg font-normal text-gray-300">/mo</span>
                </p>
                {billingStatus?.has_subscription && !billingStatus?.cancel_at_period_end && canCancelSubscription && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="text-xs text-gray-400 hover:text-white mt-2 underline"
                    data-testid="cancel-subscription-link"
                  >
                    Cancel subscription
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-testid="plan-cards">
          {displayPlans.map((plan) => {
            const status = getPlanStatus(plan.code);
            const isHighlighted = highlightPlan === plan.code;
            const PlanIcon = plan.icon;
            
            return (
              <Card 
                key={plan.code}
                className={`relative overflow-hidden transition-all duration-300 ${
                  isHighlighted ? 'ring-2 ring-electric-teal shadow-lg scale-[1.02]' : ''
                } ${status === 'current' ? 'border-2 border-electric-teal' : ''}`}
                data-testid={`plan-card-${plan.code}`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div 
                    className="absolute top-0 right-0 px-3 py-1 text-xs font-semibold text-white rounded-bl-lg"
                    style={{ backgroundColor: plan.color }}
                  >
                    {plan.badge}
                  </div>
                )}
                
                {/* Current Plan Indicator */}
                {status === 'current' && (
                  <div className="absolute top-0 left-0 right-0 bg-electric-teal text-white text-center text-xs py-1 font-medium">
                    Current Plan
                  </div>
                )}
                
                <CardHeader className={status === 'current' ? 'pt-8' : ''}>
                  <div className="flex items-center gap-3 mb-2">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${plan.color}15` }}
                    >
                      <PlanIcon className="w-5 h-5" style={{ color: plan.color }} />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{plan.name}</CardTitle>
                      <CardDescription className="text-xs">{plan.targetAudience}</CardDescription>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-midnight-blue">£{plan.monthlyPrice}</span>
                      <span className="text-gray-500">/month</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      + £{plan.onboardingFee} one-time setup
                    </p>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
                  
                  {/* Key Features */}
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span><strong>{plan.maxProperties}</strong> properties</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>
                        <strong>{featureCountForPlanBanner(plan.code)}</strong> features
                        {plan.code === currentPlan ? ' (your account)' : ' (typical tier)'}
                      </span>
                    </div>
                    {plan.code === 'PLAN_2_PORTFOLIO' && (
                      <div className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>ZIP bulk upload & scheduled PDF reports</span>
                      </div>
                    )}
                    {plan.code === 'PLAN_3_PRO' && (
                      <>
                        <div className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-500" />
                          <span>Advanced AI extraction & review</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-500" />
                          <span>Webhooks & read API</span>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* CTA Button */}
                  <Button
                    className={`w-full ${
                      status === 'current'
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : status === 'upgrade'
                          ? 'bg-electric-teal hover:bg-teal-600 text-white'
                          : 'border-amber-600 text-amber-900 bg-amber-50 hover:bg-amber-100'
                    }`}
                    variant={status === 'downgrade' ? 'outline' : 'default'}
                    onClick={() => handlePlanChange(plan.code)}
                    disabled={status === 'current' || upgrading === plan.code || !canCheckout}
                    data-testid={`upgrade-btn-${plan.code}`}
                  >
                    {upgrading === plan.code ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Processing...
                      </>
                    ) : status === 'current' ? (
                      'Current plan'
                    ) : status === 'upgrade' ? (
                      <>
                        Upgrade to {plan.name}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    ) : (
                      <>
                        Downgrade (next billing cycle)
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Feature Comparison Matrix */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="feature-matrix">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-midnight-blue">Feature Comparison</h2>
            <p className="text-sm text-gray-500 mt-1">
              Compare typical tier inclusions across plans. For <strong>your current plan</strong> column, checkmarks reflect{' '}
              <strong>your live account</strong> from the server (lifecycle and add-ons may differ from the static matrix).
            </p>
          </div>
          
          {/* Header Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-gray-50 border-b border-gray-200">
            <div className="font-medium text-gray-700">Feature</div>
            {displayPlans.map((plan) => (
              <div key={plan.code} className="text-center">
                <span 
                  className="font-semibold text-sm"
                  style={{ color: plan.color }}
                >
                  {plan.name}
                </span>
              </div>
            ))}
          </div>
          
          {/* Feature Categories */}
          {FEATURE_CATEGORIES.map((category) => {
            const CategoryIcon = category.icon;
            const isExpanded = expandedCategories[category.name];
            
            return (
              <div key={category.name} className="border-b border-gray-100 last:border-b-0">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.name)}
                  className="w-full grid grid-cols-4 gap-4 p-4 hover:bg-gray-50 transition-colors"
                  data-testid={`category-${category.name}`}
                >
                  <div className="flex items-center gap-2">
                    <CategoryIcon className="w-4 h-4 text-gray-500" />
                    <span className="font-medium text-gray-900">{category.name}</span>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  {displayPlans.map((plan) => {
                    const enabledCount = category.features.filter((f) =>
                      isFeatureEnabledForBillingComparison(plan.code, f.key)
                    ).length;
                    return (
                      <div key={plan.code} className="text-center text-sm text-gray-500">
                        {enabledCount}/{category.features.length}
                      </div>
                    );
                  })}
                </button>
                
                {/* Features */}
                {isExpanded && (
                  <div className="bg-gray-50/50">
                    {category.features.map((feature, idx) => (
                      <div 
                        key={feature.key}
                        className={`grid grid-cols-4 gap-4 px-4 py-3 ${
                          idx < category.features.length - 1 ? 'border-b border-gray-100' : ''
                        }`}
                        data-testid={`feature-row-${feature.key}`}
                      >
                        <div className="pl-6">
                          <p className="text-sm text-gray-700">{feature.name}</p>
                          <p className="text-xs text-gray-500">{feature.description}</p>
                        </div>
                        {displayPlans.map((plan) => {
                          const isEnabled = isFeatureEnabledForBillingComparison(
                            plan.code,
                            feature.key
                          );
                          return (
                            <div key={plan.code} className="flex items-center justify-center">
                              {isEnabled ? (
                                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                                  <Check className="w-4 h-4 text-green-600" />
                                </div>
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                                  <X className="w-4 h-4 text-gray-400" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="mt-12" data-testid="billing-faq">
          <h2 className="text-xl font-semibold text-midnight-blue mb-6">Frequently Asked Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-gray-900 mb-2">Can I upgrade at any time?</h3>
                <p className="text-sm text-gray-600">
                  Yes! You can upgrade your plan at any time. The new pricing will be prorated based on your billing cycle.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-gray-900 mb-2">What happens to my data if I downgrade?</h3>
                <p className="text-sm text-gray-600">
                  Your data is not deleted on downgrade. If you exceed the property limit of a lower plan, you choose which properties stay active; the rest become archived (read-only). You can view archived property data but cannot add new documents until you activate a property or upgrade.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-gray-900 mb-2">What is your refund policy?</h3>
                <p className="text-sm text-gray-600">
                  We offer a 14-day money-back guarantee on all plans. Contact support within 14 days for a full refund if you&apos;re not satisfied.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-gray-900 mb-2">How do I cancel my subscription?</h3>
                <p className="text-sm text-gray-600">
                  You can cancel anytime from your account settings or by contacting support. Your access continues until the end of your billing period.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

          </>
        )}
      </main>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="cancel-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Cancel Subscription</h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              Are you sure you want to cancel your subscription? You have two options:
            </p>

            {(cancelContextLoading || cancelContextSnapshot) && (
              <div
                className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700"
                data-testid="cancel-modal-context-snapshot"
              >
                <p className="font-medium text-gray-800 mb-2">Portfolio snapshot</p>
                {cancelContextLoading && <p className="text-gray-500">Loading…</p>}
                {!cancelContextLoading && cancelContextSnapshot && (
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>
                      Compliance:{' '}
                      {Number(cancelContextSnapshot.compliance?.requirements_overdue || 0)} overdue,{' '}
                      {Number(cancelContextSnapshot.compliance?.requirements_expiring_soon || 0)} expiring soon,{' '}
                      {Number(cancelContextSnapshot.compliance?.requirements_pending || 0)} pending
                    </li>
                    {cancelContextSnapshot.operations?.maintenance_workflows_enabled &&
                      cancelContextSnapshot.operations?.open_maintenance_issues != null && (
                        <li>Open maintenance issues: {cancelContextSnapshot.operations.open_maintenance_issues}</li>
                      )}
                    {cancelContextSnapshot.risk?.predictive_enabled &&
                      cancelContextSnapshot.risk?.active_risk_signals_count != null && (
                        <li>
                          Active risk signals: {cancelContextSnapshot.risk.active_risk_signals_count}
                          {Number(cancelContextSnapshot.risk.high_or_critical_active_count || 0) > 0
                            ? ` (${cancelContextSnapshot.risk.high_or_critical_active_count} high or critical)`
                            : ''}
                        </li>
                      )}
                  </ul>
                )}
                <p className="text-xs text-gray-500 mt-2">For your records only — you can still cancel.</p>
              </div>
            )}
            
            <div className="space-y-3 mb-6">
              <div className="p-4 border border-gray-200 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-1">Cancel at Period End</h4>
                <p className="text-sm text-gray-500">
                  Keep full access until {nextRenewalEndPhrase(billingStatus)}, then your subscription ends.
                </p>
              </div>
              <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                <h4 className="font-medium text-red-800 mb-1">Cancel Immediately</h4>
                <p className="text-sm text-red-600">
                  Lose access immediately. Your data will be preserved but features will be locked.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => handleCancelSubscription(false)}
                variant="outline"
                className="w-full"
                disabled={cancelling}
                data-testid="cancel-at-period-end-btn"
              >
                {cancelling ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Cancel at Period End
              </Button>
              <Button
                onClick={() => handleCancelSubscription(true)}
                variant="destructive"
                className="w-full bg-red-600 hover:bg-red-700"
                disabled={cancelling}
                data-testid="cancel-immediately-btn"
              >
                {cancelling ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Cancel Immediately
              </Button>
              <Button
                onClick={() => setShowCancelModal(false)}
                variant="ghost"
                className="w-full"
                disabled={cancelling}
              >
                Keep My Subscription
              </Button>
            </div>
          </div>
        </div>
      )}
      {billingStepUp.modal}
      {resumeStepUpModal}
    </PortalPageWithLifecyclePresentation>
  );
};

export default BillingPage;
